import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  useTheme,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  Skeleton,
  Pagination,
  Divider,
  IconButton,
  // Rating,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Business as BusinessIcon,
  // Visibility,
  LocationOn,
  Phone,
  WhatsApp,
  // Email,
  Verified,
  Star,
  // Event,
} from '@mui/icons-material';
import { Business } from '@/constants/types';
import { businessService } from '@/services/business/businessService';
import { ConfirmDialog } from '@/components/common';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getFileUrl } from '@/utils/businessHelpers';

const CardSkeleton: React.FC = () => (
  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
    <Skeleton variant="rectangular" height={180} />
    <CardContent>
      <Skeleton width="60%" height={24} sx={{ mb: 1 }} />
      <Skeleton width="40%" height={20} sx={{ mb: 0.5 }} />
      <Skeleton width="80%" height={20} sx={{ mb: 0.5 }} />
      <Skeleton width="50%" height={20} />
    </CardContent>
  </Card>
);

export const MyBusinessManagement: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState<Business | null>(null);

  useEffect(() => {
    loadMyBusinesses();
  }, [page]);

  const loadMyBusinesses = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await businessService.getMyBusinesses({
        page,
        limit: pageSize,
      });
      setBusinesses(response.data.businesses);
      setPagination(response.data.pagination);
    } catch (err: any) {
      setError(err.message || 'Failed to load businesses');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateClick = () => {
    navigate('/my-business/new');
  };

  const handleEditClick = (business: Business, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/my-business/edit/${business._id}`);
  };

  const handleDeleteClick = (business: Business, e: React.MouseEvent) => {
    e.stopPropagation();
    setBusinessToDelete(business);
    setDeleteDialogOpen(true);
  };

  const handleViewDetails = (business: Business) => {
    navigate(`/my-business/${business._id}`);
  };

  const handleDeleteConfirm = async () => {
    if (businessToDelete) {
      try {
        // Delete the business - backend will handle file cleanup automatically
        await businessService.deleteBusiness(businessToDelete._id);
        toast.success('Business deleted successfully');
        setDeleteDialogOpen(false);
        setBusinessToDelete(null);
        loadMyBusinesses();
      } catch (err: any) {
        toast.error(err.message || 'Failed to delete business');
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBusinessToDelete(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'in_review':
        return 'info';
      case 'clarification_needed':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const totalPages = pagination ? Math.ceil(pagination.totalItems / pageSize) : 1;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Typography variant="h4" fontWeight="bold">
          My Businesses
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateClick}
          disabled={isLoading}
        >
          Add Business
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Business Cards */}
      {isLoading ? (
        <Grid container spacing={3}>
          {Array.from({ length: pageSize }).map((_, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
              <CardSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : businesses.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 3,
          }}
        >
          <BusinessIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No businesses yet
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            Create your first business listing
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={handleCreateClick}>
            Add Business
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {businesses.map((business) => (
            <Grid key={business._id} size={{ xs: 12 }}>
              <Card
                elevation={0}
                onClick={() => handleViewDetails(business)}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: theme.shadows[4] },
                }}
              >
                {/* Top: Banner with Logo */}
                <Box sx={{ position: 'relative' }}>
                  {/* Banner */}
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: { xs: 120, sm: 150, md: 180 },
                      overflow: 'hidden',
                      bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
                    }}
                  >
                    {getFileUrl(business.bannerId) ? (
                      <Box
                        component="img"
                        src={getFileUrl(business.bannerId)}
                        alt={`${business.businessName} Banner`}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}22 0%, ${theme.palette.primary.main}44 100%)`,
                        }}
                      >
                        <BusinessIcon sx={{ fontSize: { xs: 40, md: 60 }, color: 'text.disabled' }} />
                      </Box>
                    )}
                  </Box>
                  {/* Logo - Overlapping */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -45,
                      left: 16,
                      width: 90,
                      height: 90,
                      borderRadius: '50%',
                      border: `4px solid ${theme.palette.background.paper}`,
                      bgcolor: theme.palette.mode === 'dark' ? 'grey.700' : 'white',
                      overflow: 'hidden',
                      boxShadow: 2,
                    }}
                  >
                    {getFileUrl(business.logoId) ? (
                      <CardMedia component="img" image={getFileUrl(business.logoId)} alt={business.businessName} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <BusinessIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
                      </Box>
                    )}
                  </Box>
                  {/* Verified Badge */}
                  {business.isVerified && (
                    <Chip icon={<Verified sx={{ fontSize: 14 }} />} label="Verified" size="small" color="success" sx={{ position: 'absolute', top: 8, left: 8, height: 22, fontSize: '0.7rem' }} />
                  )}
                  {/* Action Buttons - Top Right */}
                  <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={(e) => handleEditClick(business, e)} sx={{ bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'white' } }}>
                      <Edit fontSize="small" color="primary" />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => handleDeleteClick(business, e)} sx={{ bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'white' } }}>
                      <Delete fontSize="small" color="error" />
                    </IconButton>
                  </Box>
                </Box>

                {/* Content */}
                <CardContent sx={{ pt: 6.5, pb: 1.5 }}>
                  {/* Row 1: Name & Status */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="h6" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>
                      {business.businessName}
                    </Typography>
                    <Chip label={getStatusLabel(business.approvalStatus)} color={getStatusColor(business.approvalStatus)} size="small" sx={{ height: 22, fontSize: '0.7rem' }} />
                  </Box>

                  {/* Row 2: Rating, Categories */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                   {false && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, bgcolor: 'success.main', color: 'white', px: 0.8, py: 0.2, borderRadius: 1 }}>
                      <Typography variant="caption" fontWeight={600} sx={{ color: 'white' }}>{business.rating?.toFixed(1) || '0.0'}</Typography>
                      <Star sx={{ fontSize: 12 }} />
                    </Box>}
                   {false && <Typography variant="caption" color="text.secondary">({business.ratingsCount || 0})</Typography>}
                    {Array.isArray(business.businessCategory)
                      ? business.businessCategory.slice(0, 3).map((cat) => <Chip key={cat} label={cat} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />)
                      : <Chip label={business.businessCategory} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />}
                  </Box>

                  {/* Services Offered */}
                  {business.businessServices && (Array.isArray(business.businessServices) ? business.businessServices.length > 0 : !!business.businessServices) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                      {(() => {
                        const services = Array.isArray(business.businessServices)
                          ? business.businessServices
                          : [business.businessServices];
                        return (
                          <>
                            {services.slice(0, 3).map((svc) => (
                              <Chip
                                key={svc}
                                label={svc}
                                size="small"
                                color="secondary"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            ))}
                            {services.length > 3 && (
                              <Typography variant="caption" color="text.secondary">
                                +{services.length - 3} more
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  )}

                  {/* Row 3: Location & Owner */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">{business.city}, {business.state} - {business.pincode}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.disabled">Owner: {business.ownerName}</Typography>
                  </Box>

                  {/* Row 4: Description (1 line with ellipsis) */}
                  {business.description && (
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {business.description}
                    </Typography>
                  )}
                </CardContent>

                {/* Action Buttons */}
                <Divider />
                <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1 }}>
                  <Button variant="contained" startIcon={<Phone />} size="small" href={`tel:${business.contactNumber}`} onClick={(e) => e.stopPropagation()} sx={{ textTransform: 'none', bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}>
                    {business.contactNumber}
                  </Button>
                  {business.whatsappNumber && (
                    <Button variant="outlined" startIcon={<WhatsApp />} size="small" href={`https://wa.me/91${business.whatsappNumber}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} sx={{ textTransform: 'none', borderColor: 'success.main', color: 'success.main', '&:hover': { borderColor: 'success.dark' } }}>
                      WhatsApp
                    </Button>
                  )}
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Business"
        message={`Are you sure you want to delete "${businessToDelete?.businessName}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        severity="error"
      />
    </Box>
  );
};
